import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, Input } from 'antd'
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile, FormInstance } from 'antd';
import { message, Upload } from 'antd';
import { CustomRender, DeleteOutlinedIcon, FileLabel, FormItem, FormItemLabel, FormWrapperContainer, UploadedFilesLabel, UploadInfoText, VariableTag } from '../styles';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useAppDispatch, useAppSelector } from '../../utils/hooks/redux';
import { CustomUploadFile, validateUploadFiles } from '../../utils/helpers/validateUploadFiles';
import { uploadAdditionalDoc } from '../../redux/features/report/reportAsyncThunk';
import { selectUserRole } from '../../redux/features/auth/selectors';
import { checkHasPermission } from '../../utils/helpers/checkHasPermission';
import { permissions } from '../../utils/constants/permissions';

interface UploadDocsModalProps {
    form: FormInstance;
    isLoading: boolean;
}

const UploadDocsModal: React.FC<UploadDocsModalProps> = ({ form, isLoading }) => {

    const [loading, setLoading] = useState(false);
    const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<UploadFile | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([
    ]);
    const { report } = useAppSelector((state) => state.report);
    const dispatch = useAppDispatch();
    const userRole = useAppSelector(selectUserRole);

    const hasPermission = useCallback(
        (permissionKey: string) => checkHasPermission(userRole, permissionKey),
        [userRole]
    );

    const isAddPermission = useMemo(() => hasPermission(permissions.AddEditTrackingDocs), [hasPermission]);
    const isEditPermission = useMemo(() => hasPermission(permissions.AddEditTrackingDocs), [hasPermission]);
    const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteTrackingDocs), [hasPermission]);

    const handleFileChange = async ({ fileList: newFileList }: { fileList: CustomUploadFile[] }) => {
        setFileList(newFileList);
        const blobs = newFileList
            .map((file: UploadFile) => file.originFileObj ? new Blob([file.originFileObj], { type: file.originFileObj.type }) : null)
            .filter((blob) => blob !== null);
        form.setFieldsValue({ files: blobs });
    };

    const handleRemove = async (file: CustomUploadFile) => {
        const documentId = report?.additionalDocs?.find((doc: any) => doc?.id?.toString() === file?.uid);
        if (documentId && file?.uid && report?.id?.length) {
            try {
                if (report?.id) {
                    setLoading(true);
                    const formData = new FormData();
                    formData.append('deletedFiles[]', file.uid);
                    await dispatch(uploadAdditionalDoc({ lostId: report?.id, formdata: formData, isDelete: true }));

                    const filterFileList = fileList?.filter((f) => f?.uid !== file?.uid);
                    setFileList(filterFileList);
                    setShowDeleteConfirmItem(null);
                    message.success(`${file.name} has been deleted.`);
                }
            } catch (error) {
                console.log({ error })
            } finally {
                setLoading(false);
            }
        } else {
            const filterFileList = fileList.filter((f) => f.uid !== file.uid);
            setFileList(filterFileList);
            setShowDeleteConfirmItem(null);
        }
    };

    const beforeUpload = (file: any) => {
        const isValidFileType =
            file.type === 'application/pdf' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/jpg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp';

        if (!isValidFileType) {
            message.error('You can only upload PDF, JPEG, JPG, PNG, or WEBP files!');
        }
        return false;
    };

    const props: UploadProps = {
        name: 'files',
        multiple: true,
        disabled: !(isAddPermission || isEditPermission),
        accept: '.pdf,.jpeg,.jpg,.png,.webp',
        beforeUpload,
        fileList: fileList,
        listType: "text",
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
        onChange: handleFileChange,
        onRemove: handleRemove,
        itemRender: (originNode, file) => (
            <>
                {/* Show "Uploaded files" label only once, for the first file */}
                {fileList?.[0]?.uid === file.uid && (
                    <UploadedFilesLabel>Uploaded files</UploadedFilesLabel>
                )}
                <CustomRender align="center" justify='space-between'>
                    <FileLabel>{originNode}</FileLabel>
                    <Button
                        type="text"
                        icon={<DeleteOutlinedIcon />}
                        onClick={() => setShowDeleteConfirmItem(file)}
                    />
                </CustomRender>
            </>
        ),
        onDrop(e) {
            e.preventDefault();
            handleFileChange({ fileList: [...fileList] });
        },
    };

    useEffect(() => {
        if (report?.id && report?.additionalDocs && report?.additionalDocs?.length > 0) {
            setFileList(report?.additionalDocs?.map((doc: any) => ({
                uid: doc?.id?.toString(),
                name: doc?.documentName,
                status: 'done',
                url: doc?.fullUrl
            })))
        }
    }, [report])

    useEffect(() => {
        return () => {
            form.resetFields(['files']);
            setFileList([]);
        }
    }, [])

    return (
        <>
            <FormWrapperContainer disabled={loading || isLoading} form={form} name="upload_document" layout="vertical">
                <Form.Item name="deletedFiles" hidden>
                    <Input />
                </Form.Item>
                <FormItem label={<FormItemLabel>Uploaded Additional Docs (Optional)</FormItemLabel>} name="files" rules={[{ required: false, message: "" }]}>
                    <UploadInfoText color='#000'>You can upload multiple files. PDF, JPEG, JPG, PNG, WEBP are allowed.</UploadInfoText>
                    <Upload.Dragger className='upload_docs_wrapper' {...props}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload additional docs</p>
                    </Upload.Dragger>
                </FormItem>
            </FormWrapperContainer>

            {showDeleteConfirmItem && (
                <ConfirmationModal
                    handleSave={() => handleRemove(showDeleteConfirmItem)}
                    isModalOpen={!!showDeleteConfirmItem}
                    onCancel={() => setShowDeleteConfirmItem(null)}
                    title={"Confirm Delete?"}
                    message={`Are you sure you want to delete the ${showDeleteConfirmItem.name}?`}
                    confirmButtonText="Delete"
                    loading={loading}
                />
            )}
        </>
    )
}

export default UploadDocsModal;
